import React from 'react';
import { Box, Typography } from '@mui/material';

interface OEEGaugeProps {
  value: number; // 0-100
  label: string;
  size?: number; // diameter in px
  color?: string;
  thickness?: number;
  subtitle?: string;
}

const OEEGauge: React.FC<OEEGaugeProps> = ({
  value,
  label,
  size = 180,
  color = '#3B82F6',
  thickness = 14,
  subtitle,
}) => {
  const radius = (size - thickness) / 2;
  const cx = size / 2;
  const cy = size / 2;

  // 3/4 arc: starts at 135° (bottom-left), sweeps 270° clockwise to 45° (bottom-right)
  const startAngle = 135;
  const totalSweep = 270;
  const endAngle = startAngle + totalSweep;

  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const arcPath = (start: number, end: number) => {
    const x1 = cx + radius * Math.cos(toRad(start));
    const y1 = cy + radius * Math.sin(toRad(start));
    const x2 = cx + radius * Math.cos(toRad(end));
    const y2 = cy + radius * Math.sin(toRad(end));
    const sweep = end - start;
    const largeArc = sweep > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  const valueAngle = startAngle + (totalSweep * Math.min(value, 100)) / 100;

  // Color zones
  const getColor = (val: number) => {
    if (color !== '#3B82F6') return color; // custom color override
    if (val >= 85) return '#10B981'; // green
    if (val >= 60) return '#F59E0B'; // amber
    return '#EF4444'; // red
  };

  const gaugeColor = getColor(value);

  return (
    <Box sx={{ textAlign: 'center', position: 'relative', display: 'inline-block' }}>
      <svg width={size} height={size * 0.85}>
        {/* Background track */}
        <path
          d={arcPath(startAngle, endAngle)}
          fill="none"
          stroke="#374151"
          strokeWidth={thickness}
          strokeLinecap="round"
        />
        {/* Value arc */}
        {value > 0 && (
          <path
            d={arcPath(startAngle, valueAngle)}
            fill="none"
            stroke={gaugeColor}
            strokeWidth={thickness}
            strokeLinecap="round"
            style={{
              filter: `drop-shadow(0 0 6px ${gaugeColor}40)`,
            }}
          />
        )}
        {/* Tick marks at 0%, 25%, 50%, 75%, 100% */}
        {[0, 25, 50, 75, 100].map((tick) => {
          const angle = startAngle + (totalSweep * tick) / 100;
          const innerR = radius - thickness / 2 - 4;
          const outerR = radius - thickness / 2 - 10;
          const x1t = cx + innerR * Math.cos(toRad(angle));
          const y1t = cy + innerR * Math.sin(toRad(angle));
          const x2t = cx + outerR * Math.cos(toRad(angle));
          const y2t = cy + outerR * Math.sin(toRad(angle));
          return (
            <line
              key={tick}
              x1={x1t} y1={y1t} x2={x2t} y2={y2t}
              stroke="#6B7280"
              strokeWidth={1.5}
            />
          );
        })}
        {/* Center value */}
        <text
          x={cx}
          y={cy - 4}
          textAnchor="middle"
          dominantBaseline="central"
          fill="white"
          fontSize={size * 0.2}
          fontWeight="bold"
          fontFamily="Roboto, sans-serif"
        >
          {value.toFixed(1)}%
        </text>
        {/* Label below value */}
        <text
          x={cx}
          y={cy + size * 0.14}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#9CA3AF"
          fontSize={size * 0.08}
          fontFamily="Roboto, sans-serif"
        >
          {label}
        </text>
        {subtitle && (
          <text
            x={cx}
            y={cy + size * 0.22}
            textAnchor="middle"
            dominantBaseline="central"
            fill="#6B7280"
            fontSize={size * 0.065}
            fontFamily="Roboto, sans-serif"
          >
            {subtitle}
          </text>
        )}
      </svg>
    </Box>
  );
};

export default OEEGauge;
